import React, { useState, useEffect, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { 
  MessageSquare, Plus, Send, Paperclip, Search, Filter,
  Clock, CheckCircle, AlertCircle, HelpCircle, ChevronRight,
  ChevronDown, User, Headphones, FileText, CreditCard,
  Bug, Lightbulb, MessageCircle, X, Loader2, ArrowLeft
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Badge } from "../components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { 
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue 
} from "../components/ui/select";
import { 
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter 
} from "../components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { ScrollArea } from "../components/ui/scroll-area";
import { toast } from "sonner";
import { useAuthStore } from "../lib/store";

const BASE_URL = process.env.REACT_APP_BACKEND_URL || "";

const categoryIcons = {
  billing: CreditCard,
  technical: Bug,
  account: User,
  feature_request: Lightbulb,
  bug_report: Bug,
  general: MessageCircle
};

const categoryLabels = {
  billing: "Billing & Payments",
  technical: "Technical Issue",
  account: "Account & Settings",
  feature_request: "Feature Request",
  bug_report: "Bug Report",
  general: "General Inquiry"
};

const priorityColors = {
  low: "bg-gray-500/20 text-gray-400",
  medium: "bg-blue-500/20 text-blue-400",
  high: "bg-orange-500/20 text-orange-400",
  urgent: "bg-red-500/20 text-red-400"
};

const statusColors = {
  open: "bg-yellow-500/20 text-yellow-400",
  in_progress: "bg-blue-500/20 text-blue-400",
  waiting_customer: "bg-purple-500/20 text-purple-400",
  resolved: "bg-green-500/20 text-green-400",
  closed: "bg-gray-500/20 text-gray-400"
};

const statusLabels = {
  open: "Open",
  in_progress: "In Progress",
  waiting_customer: "Waiting on You",
  resolved: "Resolved",
  closed: "Closed"
};

export default function SupportPage() {
  const [activeTab, setActiveTab] = useState("tickets");
  const [tickets, setTickets] = useState([]);
  const [faq, setFaq] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showNewTicket, setShowNewTicket] = useState(false);
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [expandedFaq, setExpandedFaq] = useState(null);
  const [faqSearch, setFaqSearch] = useState("");
  
  // New ticket form
  const [newTicket, setNewTicket] = useState({
    subject: "",
    message: "",
    category: "general",
    priority: "medium",
    attachments: []
  });
  const [submitting, setSubmitting] = useState(false);
  const [uploadingAttachment, setUploadingAttachment] = useState(false);
  
  // Reply state
  const [replyMessage, setReplyMessage] = useState("");
  const [replyAttachments, setReplyAttachments] = useState([]);
  const [sendingReply, setSendingReply] = useState(false);
  const fileInputRef = useRef(null);
  const replyFileInputRef = useRef(null);
  
  const token = useAuthStore((state) => state.token);
  const messagesEndRef = useRef(null);

  useEffect(() => {
    fetchTickets();
    fetchFaq();
  }, []);

  useEffect(() => {
    if (selectedTicket) {
      messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
    }
  }, [selectedTicket?.messages]);

  const fetchTickets = async () => {
    try {
      setLoading(true);
      const res = await fetch(`${BASE_URL}/api/support/tickets`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setTickets(data.tickets || []);
      }
    } catch (error) {
      console.error("Error fetching tickets:", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchFaq = async () => {
    try {
      const res = await fetch(`${BASE_URL}/api/support/faq`);
      if (res.ok) {
        const data = await res.json();
        setFaq(data.faq || []);
      }
    } catch (error) {
      console.error("Error fetching FAQ:", error);
    }
  };

  const fetchTicketDetails = async (ticketId) => {
    try {
      const res = await fetch(`${BASE_URL}/api/support/tickets/${ticketId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setSelectedTicket(data.ticket);
      }
    } catch (error) {
      console.error("Error fetching ticket:", error);
    }
  };

  const handleFileUpload = async (file, isReply = false) => {
    if (!file) return null;
    
    // Validate file size (5MB max)
    if (file.size > 5 * 1024 * 1024) {
      toast.error("File size must be less than 5MB");
      return null;
    }
    
    // Validate file type
    const allowedTypes = ["image/png", "image/jpeg", "image/gif", "image/webp", "application/pdf", "text/plain"];
    if (!allowedTypes.includes(file.type)) {
      toast.error("Only images (PNG, JPEG, GIF, WebP), PDF, and text files are allowed");
      return null;
    }
    
    setUploadingAttachment(true);
    try {
      const formData = new FormData();
      formData.append("file", file);
      
      const res = await fetch(`${BASE_URL}/api/support/attachments/upload`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
        body: formData
      });
      
      if (res.ok) {
        const data = await res.json();
        
        if (isReply) {
          setReplyAttachments(prev => [...prev, { url: data.url, filename: data.filename }]);
        } else {
          setNewTicket(prev => ({
            ...prev,
            attachments: [...prev.attachments, data.url]
          }));
        }
        toast.success("Attachment uploaded");
        return data.url;
      } else {
        const error = await res.json();
        toast.error(error.detail || "Failed to upload attachment");
        return null;
      }
    } catch (error) {
      toast.error("Failed to upload attachment");
      return null;
    } finally {
      setUploadingAttachment(false);
    }
  };

  const handleCreateTicket = async () => {
    if (!newTicket.subject || !newTicket.message) {
      toast.error("Please fill in all required fields");
      return;
    }
    
    setSubmitting(true);
    try {
      const res = await fetch(`${BASE_URL}/api/support/tickets`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify(newTicket)
      });
      
      if (res.ok) {
        const data = await res.json();
        toast.success(`Ticket ${data.ticket.ticket_number} created!`);
        setShowNewTicket(false);
        setNewTicket({ subject: "", message: "", category: "general", priority: "medium", attachments: [] });
        fetchTickets();
        setSelectedTicket(data.ticket);
      } else {
        const error = await res.json();
        toast.error(error.detail || "Failed to create ticket");
      }
    } catch (error) {
      toast.error("Failed to create ticket");
    } finally {
      setSubmitting(false);
    }
  };

  const handleSendReply = async () => {
    if (!replyMessage.trim() || !selectedTicket) return;
    
    setSendingReply(true);
    try {
      const res = await fetch(`${BASE_URL}/api/support/tickets/${selectedTicket.id}/reply`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({ 
          message: replyMessage,
          attachments: replyAttachments.map(a => a.url)
        })
      });
      
      if (res.ok) {
        setReplyMessage("");
        setReplyAttachments([]);
        fetchTicketDetails(selectedTicket.id);
        fetchTickets();
      } else {
        toast.error("Failed to send reply");
      }
    } catch (error) {
      toast.error("Failed to send reply");
    } finally {
      setSendingReply(false);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "";
    const date = new Date(dateStr);
    return date.toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit"
    });
  };

  const filteredFaq = faq.filter(item =>
    item.question.toLowerCase().includes(faqSearch.toLowerCase()) ||
    item.answer.toLowerCase().includes(faqSearch.toLowerCase()) ||
    item.category.toLowerCase().includes(faqSearch.toLowerCase())
  );

  const faqCategories = [...new Set(faq.map(f => f.category))];

  return (
    <div className="min-h-screen bg-background p-4 md:p-6">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-6">
          <div>
            <h1 className="text-2xl md:text-3xl font-bold text-white flex items-center gap-3">
              <Headphones className="w-8 h-8 text-[#10B981]" />
              Help & Support
            </h1>
            <p className="text-muted-foreground mt-1">
              Get help with your account, billing, or technical issues
            </p>
          </div>
          
          <Button
            onClick={() => setShowNewTicket(true)}
            className="bg-[#10B981] hover:bg-[#10B981]/80"
            data-testid="new-ticket-btn"
          >
            <Plus className="w-4 h-4 mr-2" />
            New Ticket
          </Button>
        </div>

        {/* Main Content */}
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="bg-secondary/50 mb-6">
            <TabsTrigger value="tickets" className="gap-2">
              <MessageSquare className="w-4 h-4" />
              My Tickets
              {tickets.filter(t => t.status !== "closed" && t.status !== "resolved").length > 0 && (
                <Badge className="ml-1 bg-[#10B981]/20 text-[#10B981] text-xs">
                  {tickets.filter(t => t.status !== "closed" && t.status !== "resolved").length}
                </Badge>
              )}
            </TabsTrigger>
            <TabsTrigger value="faq" className="gap-2">
              <HelpCircle className="w-4 h-4" />
              FAQ
            </TabsTrigger>
          </TabsList>

          {/* Tickets Tab */}
          <TabsContent value="tickets">
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              {/* Tickets List */}
              <div className={`lg:col-span-1 ${selectedTicket ? 'hidden lg:block' : ''}`}>
                <Card className="bg-secondary/30 border-border/50">
                  <CardHeader className="pb-3">
                    <CardTitle className="text-lg text-white">Your Tickets</CardTitle>
                  </CardHeader>
                  <CardContent className="p-0">
                    {loading ? (
                      <div className="flex items-center justify-center py-12">
                        <Loader2 className="w-6 h-6 animate-spin text-[#10B981]" />
                      </div>
                    ) : tickets.length === 0 ? (
                      <div className="text-center py-12 px-4">
                        <MessageSquare className="w-12 h-12 mx-auto text-muted-foreground/50 mb-3" />
                        <p className="text-muted-foreground">No tickets yet</p>
                        <Button
                          variant="link"
                          onClick={() => setShowNewTicket(true)}
                          className="text-[#10B981] mt-2"
                        >
                          Create your first ticket
                        </Button>
                      </div>
                    ) : (
                      <ScrollArea className="h-[500px]">
                        {tickets.map((ticket) => {
                          const CategoryIcon = categoryIcons[ticket.category] || MessageCircle;
                          return (
                            <button
                              key={ticket.id}
                              onClick={() => fetchTicketDetails(ticket.id)}
                              className={`w-full p-4 text-left border-b border-border/30 hover:bg-secondary/50 transition-colors ${
                                selectedTicket?.id === ticket.id ? 'bg-secondary/50' : ''
                              }`}
                              data-testid={`ticket-${ticket.id}`}
                            >
                              <div className="flex items-start gap-3">
                                <div className={`p-2 rounded-lg ${
                                  ticket.status === 'waiting_customer' ? 'bg-purple-500/20' : 'bg-secondary/50'
                                }`}>
                                  <CategoryIcon className="w-4 h-4 text-muted-foreground" />
                                </div>
                                <div className="flex-1 min-w-0">
                                  <div className="flex items-center gap-2 mb-1">
                                    <span className="text-xs text-muted-foreground font-mono">
                                      {ticket.ticket_number}
                                    </span>
                                    <Badge className={`text-xs ${statusColors[ticket.status]}`}>
                                      {statusLabels[ticket.status]}
                                    </Badge>
                                  </div>
                                  <p className="text-sm text-white font-medium truncate">
                                    {ticket.subject}
                                  </p>
                                  <p className="text-xs text-muted-foreground mt-1">
                                    {formatDate(ticket.updated_at)}
                                  </p>
                                </div>
                                <ChevronRight className="w-4 h-4 text-muted-foreground" />
                              </div>
                            </button>
                          );
                        })}
                      </ScrollArea>
                    )}
                  </CardContent>
                </Card>
              </div>

              {/* Ticket Details / Conversation */}
              <div className={`lg:col-span-2 ${!selectedTicket ? 'hidden lg:block' : ''}`}>
                {selectedTicket ? (
                  <Card className="bg-secondary/30 border-border/50 h-[600px] flex flex-col">
                    {/* Ticket Header */}
                    <CardHeader className="border-b border-border/30 pb-4">
                      <div className="flex items-start gap-3">
                        <Button
                          variant="ghost"
                          size="sm"
                          className="lg:hidden -ml-2"
                          onClick={() => setSelectedTicket(null)}
                        >
                          <ArrowLeft className="w-4 h-4" />
                        </Button>
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-1 flex-wrap">
                            <span className="text-sm text-muted-foreground font-mono">
                              {selectedTicket.ticket_number}
                            </span>
                            <Badge className={`text-xs ${statusColors[selectedTicket.status]}`}>
                              {statusLabels[selectedTicket.status]}
                            </Badge>
                            <Badge className={`text-xs ${priorityColors[selectedTicket.priority]}`}>
                              {selectedTicket.priority}
                            </Badge>
                          </div>
                          <CardTitle className="text-lg text-white">
                            {selectedTicket.subject}
                          </CardTitle>
                          <p className="text-sm text-muted-foreground mt-1">
                            {categoryLabels[selectedTicket.category]} • Created {formatDate(selectedTicket.created_at)}
                          </p>
                        </div>
                      </div>
                    </CardHeader>

                    {/* Messages */}
                    <ScrollArea className="flex-1 p-4">
                      <div className="space-y-4">
                        {selectedTicket.messages?.map((msg, idx) => (
                          <div
                            key={msg.id || idx}
                            className={`flex ${msg.sender_type === 'user' ? 'justify-end' : 'justify-start'}`}
                          >
                            <div className={`max-w-[80%] ${
                              msg.sender_type === 'user'
                                ? 'bg-[#10B981]/20 border-[#10B981]/30'
                                : 'bg-secondary/50 border-border/30'
                            } border rounded-lg p-3`}>
                              <div className="flex items-center gap-2 mb-1">
                                <span className={`text-xs font-medium ${
                                  msg.sender_type === 'user' ? 'text-[#10B981]' : 'text-[#A855F7]'
                                }`}>
                                  {msg.sender_type === 'user' ? 'You' : 'Support Team'}
                                </span>
                                <span className="text-xs text-muted-foreground">
                                  {formatDate(msg.created_at)}
                                </span>
                              </div>
                              <p className="text-sm text-white whitespace-pre-wrap">
                                {msg.message}
                              </p>
                            </div>
                          </div>
                        ))}
                        <div ref={messagesEndRef} />
                      </div>
                    </ScrollArea>

                    {/* Reply Box */}
                    {selectedTicket.status !== 'closed' && (
                      <div className="p-4 border-t border-border/30">
                        <div className="flex gap-2">
                          <Textarea
                            placeholder="Type your message..."
                            value={replyMessage}
                            onChange={(e) => setReplyMessage(e.target.value)}
                            className="bg-secondary/30 border-border/50 resize-none min-h-[60px]"
                            onKeyDown={(e) => {
                              if (e.key === 'Enter' && !e.shiftKey) {
                                e.preventDefault();
                                handleSendReply();
                              }
                            }}
                          />
                          <Button
                            onClick={handleSendReply}
                            disabled={sendingReply || !replyMessage.trim()}
                            className="bg-[#10B981] hover:bg-[#10B981]/80 self-end"
                          >
                            {sendingReply ? (
                              <Loader2 className="w-4 h-4 animate-spin" />
                            ) : (
                              <Send className="w-4 h-4" />
                            )}
                          </Button>
                        </div>
                      </div>
                    )}
                  </Card>
                ) : (
                  <Card className="bg-secondary/30 border-border/50 h-[600px] flex items-center justify-center">
                    <div className="text-center px-4">
                      <MessageSquare className="w-16 h-16 mx-auto text-muted-foreground/30 mb-4" />
                      <p className="text-muted-foreground">
                        Select a ticket to view the conversation
                      </p>
                    </div>
                  </Card>
                )}
              </div>
            </div>
          </TabsContent>

          {/* FAQ Tab */}
          <TabsContent value="faq">
            <div className="space-y-6">
              {/* Search */}
              <div className="relative max-w-md">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input
                  placeholder="Search FAQ..."
                  value={faqSearch}
                  onChange={(e) => setFaqSearch(e.target.value)}
                  className="bg-secondary/30 border-border/50 pl-9"
                />
              </div>

              {/* FAQ by Category */}
              {faqCategories.map((category) => {
                const categoryFaq = filteredFaq.filter(f => f.category === category);
                if (categoryFaq.length === 0) return null;
                
                return (
                  <div key={category}>
                    <h3 className="text-lg font-semibold text-white mb-3 flex items-center gap-2">
                      <FileText className="w-5 h-5 text-[#10B981]" />
                      {category}
                    </h3>
                    <div className="space-y-2">
                      {categoryFaq.map((item) => (
                        <Card
                          key={item.id}
                          className="bg-secondary/30 border-border/50 overflow-hidden"
                        >
                          <button
                            onClick={() => setExpandedFaq(expandedFaq === item.id ? null : item.id)}
                            className="w-full p-4 text-left flex items-center justify-between hover:bg-secondary/50 transition-colors"
                          >
                            <span className="text-white font-medium pr-4">{item.question}</span>
                            <ChevronDown className={`w-5 h-5 text-muted-foreground transition-transform ${
                              expandedFaq === item.id ? 'rotate-180' : ''
                            }`} />
                          </button>
                          <AnimatePresence>
                            {expandedFaq === item.id && (
                              <motion.div
                                initial={{ height: 0, opacity: 0 }}
                                animate={{ height: "auto", opacity: 1 }}
                                exit={{ height: 0, opacity: 0 }}
                                className="border-t border-border/30"
                              >
                                <p className="p-4 text-muted-foreground whitespace-pre-wrap">
                                  {item.answer}
                                </p>
                              </motion.div>
                            )}
                          </AnimatePresence>
                        </Card>
                      ))}
                    </div>
                  </div>
                );
              })}

              {/* Contact Support CTA */}
              <Card className="bg-gradient-to-r from-[#10B981]/20 to-[#A855F7]/20 border-[#10B981]/30">
                <CardContent className="p-6 flex flex-col md:flex-row items-center gap-4">
                  <div className="flex-1 text-center md:text-left">
                    <h3 className="text-lg font-semibold text-white mb-1">
                      Can't find what you're looking for?
                    </h3>
                    <p className="text-muted-foreground">
                      Our support team is here to help. Create a ticket and we'll get back to you soon.
                    </p>
                  </div>
                  <Button
                    onClick={() => {
                      setActiveTab("tickets");
                      setShowNewTicket(true);
                    }}
                    className="bg-[#10B981] hover:bg-[#10B981]/80"
                  >
                    <MessageSquare className="w-4 h-4 mr-2" />
                    Contact Support
                  </Button>
                </CardContent>
              </Card>
            </div>
          </TabsContent>
        </Tabs>

        {/* New Ticket Modal */}
        <Dialog open={showNewTicket} onOpenChange={setShowNewTicket}>
          <DialogContent className="bg-card border-border/50 max-w-lg">
            <DialogHeader>
              <DialogTitle className="text-xl font-semibold text-white flex items-center gap-2">
                <Plus className="w-5 h-5 text-[#10B981]" />
                Create Support Ticket
              </DialogTitle>
              <DialogDescription>
                Describe your issue and we'll get back to you as soon as possible.
              </DialogDescription>
            </DialogHeader>
            
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <label className="text-sm text-white">Category</label>
                <Select
                  value={newTicket.category}
                  onValueChange={(v) => setNewTicket({ ...newTicket, category: v })}
                >
                  <SelectTrigger className="bg-secondary/30 border-border/50">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {Object.entries(categoryLabels).map(([key, label]) => {
                      const Icon = categoryIcons[key];
                      return (
                        <SelectItem key={key} value={key}>
                          <span className="flex items-center gap-2">
                            <Icon className="w-4 h-4" />
                            {label}
                          </span>
                        </SelectItem>
                      );
                    })}
                  </SelectContent>
                </Select>
              </div>
              
              <div className="space-y-2">
                <label className="text-sm text-white">Priority</label>
                <Select
                  value={newTicket.priority}
                  onValueChange={(v) => setNewTicket({ ...newTicket, priority: v })}
                >
                  <SelectTrigger className="bg-secondary/30 border-border/50">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="low">Low - General question</SelectItem>
                    <SelectItem value="medium">Medium - Need help soon</SelectItem>
                    <SelectItem value="high">High - Affecting my work</SelectItem>
                    <SelectItem value="urgent">Urgent - Can't use the service</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              <div className="space-y-2">
                <label className="text-sm text-white">Subject *</label>
                <Input
                  placeholder="Brief description of your issue"
                  value={newTicket.subject}
                  onChange={(e) => setNewTicket({ ...newTicket, subject: e.target.value })}
                  className="bg-secondary/30 border-border/50"
                />
              </div>
              
              <div className="space-y-2">
                <label className="text-sm text-white">Message *</label>
                <Textarea
                  placeholder="Describe your issue in detail. Include any error messages, steps to reproduce, etc."
                  value={newTicket.message}
                  onChange={(e) => setNewTicket({ ...newTicket, message: e.target.value })}
                  className="bg-secondary/30 border-border/50 min-h-[120px]"
                />
              </div>
              
              {/* Attachments */}
              <div className="space-y-2">
                <label className="text-sm font-medium flex items-center gap-2">
                  <Paperclip className="w-4 h-4" />
                  Attachments (optional)
                </label>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*,.pdf,.txt"
                  className="hidden"
                  onChange={(e) => {
                    if (e.target.files?.[0]) {
                      handleFileUpload(e.target.files[0], false);
                    }
                    e.target.value = "";
                  }}
                />
                <div className="flex flex-wrap items-center gap-2">
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => fileInputRef.current?.click()}
                    disabled={uploadingAttachment || newTicket.attachments.length >= 3}
                    className="gap-2"
                  >
                    {uploadingAttachment ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      <Paperclip className="w-4 h-4" />
                    )}
                    Add File
                  </Button>
                  {newTicket.attachments.length > 0 && (
                    <span className="text-xs text-muted-foreground">
                      {newTicket.attachments.length}/3 files
                    </span>
                  )}
                </div>
                {newTicket.attachments.length > 0 && (
                  <div className="flex flex-wrap gap-2 mt-2">
                    {newTicket.attachments.map((url, idx) => (
                      <div key={idx} className="flex items-center gap-1 bg-secondary/50 rounded px-2 py-1 text-xs">
                        <FileText className="w-3 h-3" />
                        <span className="truncate max-w-[100px]">File {idx + 1}</span>
                        <button
                          type="button"
                          onClick={() => setNewTicket(prev => ({
                            ...prev,
                            attachments: prev.attachments.filter((_, i) => i !== idx)
                          }))}
                          className="ml-1 hover:text-red-400"
                        >
                          <X className="w-3 h-3" />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
                <p className="text-xs text-muted-foreground">Max 3 files, 5MB each. Images, PDF, or text files.</p>
              </div>
            </div>
            
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowNewTicket(false)}>
                Cancel
              </Button>
              <Button
                onClick={handleCreateTicket}
                disabled={submitting || !newTicket.subject || !newTicket.message}
                className="bg-[#10B981] hover:bg-[#10B981]/80"
              >
                {submitting ? (
                  <Loader2 className="w-4 h-4 animate-spin mr-2" />
                ) : (
                  <Send className="w-4 h-4 mr-2" />
                )}
                Submit Ticket
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
}
